import type {
  ComplianceLetterDraftResponse,
  ComplianceLetterGenerateRequest,
  FileResponse,
  GetInspectionListRequestV2,
  GetInspectionListResponse,
  InspectionAssignRequest,
  InspectionAssigneeCandidateResponse,
  InspectionDeviceHistoryEntryResponse,
  InspectionDispatchBoardResponse,
  InspectionDispatchCommitRequest,
  InspectionOfflineBundleResponse,
  InspectionRequest,
  InspectionResponse,
  InspectionTransitionRequest,
} from 'src/api/ProactApiClient';
import {
  inspectionApiClient,
  inspectionDispatchApiClient,
} from 'src/api/ProactApiClientInstances';
import {applyTransition} from 'src/app/inspections/offline/appliers';
import {INSPECTION_OUTBOX_KINDS} from 'src/app/inspections/offline/executors';
import type {InspectionDeviceHistoryArg} from 'src/app/inspections/offline/reads';
import {
  inspectionDeviceHistoryQueryFn,
  inspectionMetaQueryFn,
} from 'src/app/inspections/offline/reads';
import {inspectionContextKey} from 'src/app/inspections/offline/snapshotKeys';
import {
  isOfflineQueuedResult,
  offlineMutation,
} from 'src/offline/offlineMutation';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

const INSPECTION_TAGS = ['Inspections'] as const;

// RTK Query args must be serializable — the dispatch window is passed as ISO
// strings and rebuilt into the client's Date-typed request at the boundary.
export interface DispatchBoardArgs {
  fromUtc: string;
  toUtc: string;
}

// Newtonsoft sends explicit `null`, generated client types `| undefined` —
// normalize once here (see also inspectionRecord.ts normalizers).
const normalizeDeviceHistoryEntry = (
  entry: InspectionDeviceHistoryEntryResponse
): InspectionDeviceHistoryEntryResponse => ({
  ...entry,
  inspectionDate: entry.inspectionDate ?? undefined,
  complianceState: entry.complianceState ?? undefined,
});

const normalizeScheduleLink = <
  T extends Pick<
    InspectionResponse,
    | 'inspectionScheduleId'
    | 'inspectionScheduleCadence'
    | 'inspectionScheduleIntervalDays'
  >,
>(
  inspection: T
): T => ({
  ...inspection,
  inspectionScheduleId: inspection.inspectionScheduleId ?? undefined,
  inspectionScheduleCadence: inspection.inspectionScheduleCadence ?? undefined,
  inspectionScheduleIntervalDays:
    inspection.inspectionScheduleIntervalDays ?? undefined,
});

const normalizeDispatchBoard = (
  board: InspectionDispatchBoardResponse
): InspectionDispatchBoardResponse => ({
  queue: board.queue.map(q => ({
    ...q,
    clientId: q.clientId ?? undefined,
    clientName: q.clientName ?? undefined,
    dueAt: q.dueAt ?? undefined,
  })),
  inspectors: board.inspectors,
  committed: board.committed.map(c => ({
    ...c,
    assigneeId: c.assigneeId ?? undefined,
    assigneeName: c.assigneeName ?? undefined,
    scheduledStartAt: c.scheduledStartAt ?? undefined,
    dueAt: c.dueAt ?? undefined,
  })),
});

export const inspectionApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getInspectionListV2: builder.query<
      GetInspectionListResponse,
      GetInspectionListRequestV2
    >({
      queryFn: clientQuery(async params => {
        const response = await inspectionApiClient.listV2(params);
        return {
          ...response,
          data: (response.data ?? []).map(normalizeScheduleLink),
        };
      }),
      providesTags: ['Inspections'],
    }),
    getInspection: builder.query<InspectionResponse, number>({
      queryFn: inspectionMetaQueryFn(async id =>
        normalizeScheduleLink(await inspectionApiClient.get(id))
      ),
      providesTags: ['Inspections'],
    }),
    getInspectionAssigneeCandidates: builder.query<
      InspectionAssigneeCandidateResponse[],
      void
    >({
      queryFn: clientQuery(() => inspectionApiClient.getAssigneeCandidates()),
      providesTags: ['Inspections'],
    }),
    getInspectionDeviceHistory: builder.query<
      InspectionDeviceHistoryEntryResponse[],
      InspectionDeviceHistoryArg
    >({
      queryFn: inspectionDeviceHistoryQueryFn(async deviceId => {
        const entries = await inspectionApiClient.getDeviceHistory(deviceId);
        return entries.map(normalizeDeviceHistoryEntry);
      }),
      providesTags: ['Inspections'],
    }),
    // A mutation, not a query: the bundle is large and consumed once by the
    // offline download, so it must not linger in the RTK cache.
    fetchInspectionOfflineBundle: builder.mutation<
      InspectionOfflineBundleResponse,
      number
    >({
      queryFn: clientQuery(id => inspectionApiClient.getOfflineBundle(id)),
    }),
    createInspection: builder.mutation<InspectionResponse, InspectionRequest>({
      queryFn: clientQuery(request => inspectionApiClient.create(request)),
      invalidatesTags: [...INSPECTION_TAGS, 'InspectionSchedules'],
    }),
    updateInspection: builder.mutation<InspectionResponse, InspectionRequest>({
      queryFn: clientQuery(request => inspectionApiClient.update(request)),
      invalidatesTags: [...INSPECTION_TAGS],
    }),
    deleteInspection: builder.mutation<boolean, number>({
      queryFn: clientQuery(id => inspectionApiClient.delete(id)),
      invalidatesTags: [...INSPECTION_TAGS, 'InspectionSchedules'],
    }),
    assignInspection: builder.mutation<
      InspectionResponse,
      {id: number; request: InspectionAssignRequest}
    >({
      queryFn: clientQuery(({id, request}) =>
        inspectionApiClient.assign(id, request)
      ),
      invalidatesTags: [...INSPECTION_TAGS],
    }),
    unassignInspection: builder.mutation<InspectionResponse, number>({
      queryFn: clientQuery(id => inspectionApiClient.unassign(id)),
      invalidatesTags: [...INSPECTION_TAGS],
    }),
    getDispatchBoard: builder.query<
      InspectionDispatchBoardResponse,
      DispatchBoardArgs
    >({
      queryFn: clientQuery(async ({fromUtc, toUtc}) =>
        normalizeDispatchBoard(
          await inspectionDispatchApiClient.getBoard({
            fromUtc: new Date(fromUtc),
            toUtc: new Date(toUtc),
          })
        )
      ),
      providesTags: ['Inspections'],
    }),
    commitDispatch: builder.mutation<void, InspectionDispatchCommitRequest>({
      queryFn: clientQuery(async request => {
        await inspectionDispatchApiClient.commit(request);
      }),
      invalidatesTags: [...INSPECTION_TAGS],
    }),
    getComplianceLetterDraft: builder.query<
      ComplianceLetterDraftResponse,
      number
    >({
      queryFn: clientQuery(id => inspectionApiClient.getLetterDraft(id)),
    }),
    generateComplianceLetter: builder.mutation<
      FileResponse | null,
      {id: number; request: ComplianceLetterGenerateRequest; timeZone: string}
    >({
      queryFn: clientQuery(({id, request, timeZone}) =>
        inspectionApiClient.generateLetter(id, request, timeZone)
      ),
    }),
    generateInspectionFormPdf: builder.mutation<
      FileResponse | null,
      {id: number; timeZone: string}
    >({
      queryFn: clientQuery(({id, timeZone}) =>
        inspectionApiClient.generateForm(id, timeZone)
      ),
    }),
    transitionInspection: builder.mutation<
      InspectionResponse,
      {id: number; request: InspectionTransitionRequest}
    >({
      queryFn: offlineMutation(
        INSPECTION_OUTBOX_KINDS.transition,
        ({id, request}) => inspectionApiClient.transition(id, request),
        {
          contextKey: ({id}) => inspectionContextKey(id),
          applyLocal: applyTransition,
        }
      ),
      // Deficiency refetch would hit the network; drop it when queued offline.
      invalidatesTags: result =>
        isOfflineQueuedResult(result)
          ? ['Inspections', 'InspectionDevices']
          : [
              'Inspections',
              'InspectionDevices',
              'Deficiencies',
              'InspectionSchedules',
            ],
    }),
  }),
});

export const {
  useGetInspectionListV2Query,
  useGetInspectionQuery,
  useGetInspectionAssigneeCandidatesQuery,
  useGetInspectionDeviceHistoryQuery,
  useFetchInspectionOfflineBundleMutation,
  useCreateInspectionMutation,
  useUpdateInspectionMutation,
  useDeleteInspectionMutation,
  useAssignInspectionMutation,
  useUnassignInspectionMutation,
  useGetDispatchBoardQuery,
  useCommitDispatchMutation,
  useTransitionInspectionMutation,
  useLazyGetComplianceLetterDraftQuery,
  useGenerateComplianceLetterMutation,
  useGenerateInspectionFormPdfMutation,
} = inspectionApi;
